/**
 * F-506-FE-06: SessionEndPage
 *
 * Transition screen at /session/:id/end.
 * - Triggers ENDING → CLOSED state transition via PATCH /api/sessions/:id/transition
 * - Shows session summary: duration, segment count, flag count
 * - Progress indicator for note generation (polls GET /api/sessions/:id/note status)
 * - Navigates to /notes/:noteId/decision when note is ready
 *
 * Design: Brief, summary-focused, guides to next action.
 * Mobile-first. Calm, reassuring tone.
 */

import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SessionSummary {
  id: string;
  status: string;
  createdAt: string;
  endedAt?: string;
  segments: Array<{ id: string; safetyLevel?: string }>;
  noteId?: string;
  noteStatus?: string;
}

interface NoteStatusResponse {
  noteId: string;
  status: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDurationFromMs(ms: number): string {
  const totalSecs = Math.floor(ms / 1_000);
  const h = Math.floor(totalSecs / 3_600);
  const m = Math.floor((totalSecs % 3_600) / 60);
  const s = totalSecs % 60;
  if (h > 0) {
    return `${h}h ${m}m ${s}s`;
  }
  return `${m}m ${s}s`;
}

// ---------------------------------------------------------------------------
// Summary stat card
// ---------------------------------------------------------------------------

interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ReactNode;
}

function StatCard({ label, value, icon }: StatCardProps) {
  return (
    <div className="clinical-card p-5 flex items-start gap-4">
      <div className="w-10 h-10 rounded-full bg-clinical-cream-dark flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div>
        <p className="text-2xl font-heading font-semibold text-clinical-charcoal leading-none">
          {value}
        </p>
        <p className="text-xs font-body text-clinical-text-muted mt-1">{label}</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function SessionEndPage() {
  const { id: sessionId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [transitionDone, setTransitionDone] = useState(false);
  const [transitionError, setTransitionError] = useState<string | null>(null);
  const hasTriggeredRef = useRef(false);

  // ---------------------------------------------------------------------------
  // Fetch session summary
  // ---------------------------------------------------------------------------
  const { data: session } = useQuery<SessionSummary>({
    queryKey: ['session-summary', sessionId],
    queryFn: () => api.get<SessionSummary>(`/api/sessions/${sessionId ?? ''}`),
    enabled: Boolean(sessionId),
  });

  // ---------------------------------------------------------------------------
  // Trigger ENDING → CLOSED transition on mount (once)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!sessionId || hasTriggeredRef.current) return;
    hasTriggeredRef.current = true;

    api
      .patch(`/api/sessions/${sessionId}/transition`, { targetStatus: 'CLOSED' })
      .then(() => setTransitionDone(true))
      .catch((err: unknown) => {
        const e = err as Error & { status?: number };
        // 409 means already in CLOSED/NOTE_REVIEW — that's fine
        if (e.status === 409) {
          setTransitionDone(true);
        } else {
          setTransitionError('Could not close session. Please try again.');
        }
      });
  }, [sessionId]);

  // ---------------------------------------------------------------------------
  // Poll note status after transition is done
  // ---------------------------------------------------------------------------
  const { data: noteStatus } = useQuery<NoteStatusResponse>({
    queryKey: ['session-note-status', sessionId],
    queryFn: () => api.get<NoteStatusResponse>(`/api/sessions/${sessionId ?? ''}/note`),
    enabled: Boolean(sessionId) && transitionDone,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (!status) return 2_000; // Not yet returned — poll fast
      if (status === 'GENERATING') return 3_000;
      return false; // Stop polling when note is ready
    },
  });

  // ---------------------------------------------------------------------------
  // Navigate to note when ready
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (noteStatus?.noteId && noteStatus.status !== 'GENERATING') {
      navigate(`/notes/${noteStatus.noteId}/decision`, { replace: true });
    }
  }, [noteStatus, navigate]);

  // ---------------------------------------------------------------------------
  // Derive stats
  // ---------------------------------------------------------------------------
  const segmentCount = session?.segments?.length ?? 0;
  const flagCount =
    session?.segments?.filter(
      (s) => s.safetyLevel === 'amber' || s.safetyLevel === 'red',
    ).length ?? 0;

  let durationDisplay = '—';
  if (session?.createdAt) {
    const start = new Date(session.createdAt).getTime();
    const end = session.endedAt ? new Date(session.endedAt).getTime() : Date.now();
    durationDisplay = formatDurationFromMs(end - start);
  }

  // ---------------------------------------------------------------------------
  // Note generation status label
  // ---------------------------------------------------------------------------
  function getNoteStatusLabel(): string {
    const s = noteStatus?.status;
    if (!s) return 'Waiting...';
    if (s === 'GENERATING') return 'Generating clinical note...';
    if (s === 'DRAFT') return 'Note ready for review';
    if (s === 'GENERATION_FAILED') return 'Note generation failed';
    return s;
  }

  const isGenerating = !noteStatus?.status || noteStatus.status === 'GENERATING';

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-[var(--color-bg)] flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-clinical-charcoal flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-white"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
              />
            </svg>
          </div>
          <h1 className="font-heading text-2xl font-semibold text-clinical-charcoal">
            Session complete
          </h1>
          <p className="text-sm font-body text-clinical-text-muted mt-1">
            Your consultation has ended
          </p>
        </div>

        {/* Session stats */}
        <div className="space-y-3 mb-6">
          <StatCard
            label="Duration"
            value={durationDisplay}
            icon={
              <svg className="w-5 h-5 text-clinical-teal" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
              </svg>
            }
          />
          <StatCard
            label="Translated exchanges"
            value={segmentCount}
            icon={
              <svg className="w-5 h-5 text-clinical-teal" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 9.75a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 0 1 .778-.332 48.294 48.294 0 0 0 5.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z" />
              </svg>
            }
          />
          {flagCount > 0 && (
            <StatCard
              label="Safety flags raised"
              value={flagCount}
              icon={
                <svg className="w-5 h-5 text-safety-amber" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                  <path d="M6.457 1.047c.659-1.234 2.427-1.234 3.086 0l6.082 11.378A1.75 1.75 0 0 1 14.082 15H1.918a1.75 1.75 0 0 1-1.543-2.575Zm1.763.707a.25.25 0 0 0-.44 0L1.698 13.132a.25.25 0 0 0 .22.368h12.164a.25.25 0 0 0 .22-.368Zm.53 3.996v2.5a.75.75 0 0 1-1.5 0v-2.5a.75.75 0 0 1 1.5 0ZM9 11a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z" />
                </svg>
              }
            />
          )}
        </div>

        {/* Note generation progress */}
        <div className="clinical-card p-5">
          <div className="flex items-center gap-3">
            {isGenerating ? (
              <span
                className="w-5 h-5 border-2 border-clinical-teal border-t-transparent rounded-full animate-spin flex-shrink-0"
                aria-hidden="true"
              />
            ) : noteStatus?.status === 'GENERATION_FAILED' ? (
              <svg className="w-5 h-5 flex-shrink-0 text-safety-red" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M8 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13ZM0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8Zm4.22-2.78a.75.75 0 0 1 1.06 0L8 7.94l2.72-2.72a.75.75 0 1 1 1.06 1.06L9.06 9l2.72 2.72a.75.75 0 0 1-1.06 1.06L8 10.06l-2.72 2.72a.75.75 0 0 1-1.06-1.06L6.94 9 4.22 6.28a.75.75 0 0 1 0-1.06Z" />
              </svg>
            ) : (
              <svg className="w-5 h-5 flex-shrink-0 text-safety-green" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z" />
              </svg>
            )}
            <div>
              <p
                className="text-sm font-body font-semibold text-clinical-charcoal"
                aria-live="polite"
              >
                Clinical note
              </p>
              <p className="text-xs font-body text-clinical-text-muted mt-0.5">
                {getNoteStatusLabel()}
              </p>
            </div>
          </div>

          {/* Animated progress bar while generating */}
          {isGenerating && (
            <div
              className="mt-3 h-1 rounded-full bg-clinical-cream-dark overflow-hidden"
              role="progressbar"
              aria-label="Generating clinical note"
            >
              <div className="h-full bg-clinical-teal rounded-full animate-progressSlide" />
            </div>
          )}
        </div>

        {/* Transition error */}
        {transitionError && (
          <div
            role="alert"
            className="mt-4 p-3 rounded-clinical bg-red-50 border border-red-200 text-sm font-body text-red-700"
          >
            {transitionError}
          </div>
        )}

        {/* Back to dashboard */}
        <button
          onClick={() => navigate('/dashboard')}
          className="btn-secondary w-full mt-6"
        >
          Back to dashboard
        </button>
      </div>
    </div>
  );
}
